import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapPin, Briefcase, Users, Building2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import client from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

export default function JobDetail() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [applying, setApplying] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    client.get(`/public/jobs/${id}`).then((r) => setJob(r.data)).catch(() => nav("/jobs"));
  }, [id, nav]);

  const apply = async () => {
    if (!user) {
      toast.info("Please login or register as a candidate to apply.");
      nav("/login");
      return;
    }
    if (user.role !== "candidate") {
      toast.error("Only candidate accounts can apply to jobs.");
      return;
    }
    setApplying(true);
    try {
      await client.post(`/me/candidate/apply/${id}`);
      toast.success("Application submitted! Track it in your dashboard.");
      nav("/app/candidate/applications");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not apply");
    } finally {
      setApplying(false);
    }
  };

  if (!job) return <div className="max-w-4xl mx-auto px-6 py-20 text-slate-400">Loading...</div>;

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <button onClick={() => nav("/jobs")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#0088FF] mb-6" data-testid="job-back-btn">
        <ArrowLeft className="h-4 w-4" /> Back to jobs
      </button>
      <div className="bg-white rounded-2xl border border-slate-200 p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl font-extrabold text-[#0A192F]">{job.title}</h1>
            <p className="text-slate-500 mt-1 flex items-center gap-1.5"><Building2 className="h-4 w-4" /> {job.client_name}</p>
          </div>
          <div className="text-right">
            <div className="font-display text-xl font-bold text-[#0088FF]">{job.salary}</div>
            <div className="text-xs text-slate-400">{job.code}</div>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mt-6">
          {[
            { icon: MapPin, label: "Location", value: job.country },
            { icon: Briefcase, label: "Experience", value: job.experience },
            { icon: Users, label: "Vacancies", value: job.vacancies },
          ].map((d) => (
            <div key={d.label} className="bg-slate-50 rounded-xl p-4 border border-slate-100">
              <div className="flex items-center gap-1.5 text-xs text-slate-400"><d.icon className="h-4 w-4" /> {d.label}</div>
              <div className="font-semibold text-[#0A192F] mt-1">{d.value}</div>
            </div>
          ))}
        </div>

        <div className="mt-8">
          <h3 className="font-display font-semibold text-lg text-[#0A192F] mb-2">Requirements</h3>
          <p className="text-slate-600 leading-relaxed">{job.requirements}</p>
          {job.skills && (
            <div className="mt-4 flex items-center gap-2 text-sm">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" /> <span className="text-slate-600">Skills: {job.skills}</span>
            </div>
          )}
        </div>

        <div className="mt-8 pt-6 border-t border-slate-100">
          <Button data-testid="apply-job-btn" size="lg" disabled={applying} onClick={apply} className="w-full sm:w-auto bg-[#0088FF] hover:bg-[#0070E0] h-12 px-10">
            {applying ? "Submitting..." : "Apply Now"}
          </Button>
        </div>
      </div>
    </div>
  );
}
